import { Popover } from '@headlessui/react'
import { Float } from '@headlessui-float/react'
import { useEffect, useState } from 'react'
import styled, { css } from 'styled-components'
import { Button } from '../button'
import { Feedback } from '../feedback'
import { Icon } from '../icon'
import { TextInput } from '../text-input'
import { Tooltip } from '../tooltip'
import { filterTriggerStyles } from '../filter-list/filter-trigger.styles'
import type { FilterRangeProps, FilterRangeValue } from './filter-range.types'

const StyledTrigger = styled(Popover.Button)`
  ${filterTriggerStyles}
`

const StyledPanel = styled(Popover.Panel)`
  ${({ theme }) => css`
    z-index: 10;
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xs};
    padding: ${theme.spacing.xs};
    background: ${theme.semantic.backgroundSecondary};
    box-shadow: ${theme.boxShadows.medium};
    border-radius: ${theme.radii.container};
  `}
`

const StyledFields = styled.div`
  ${({ theme }) => css`
    display: flex;
    align-items: flex-end;
    gap: ${theme.spacing.xs};
  `}
`

const StyledField = styled.label`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.spacing.xxs};
    /* no width token covers a form field; keeps both number inputs equal and compact */
    width: 104px;
    font-size: ${theme.fontSizes.bodySmall};
    font-weight: ${theme.fontWeights.medium};
    color: ${theme.semantic.contentPrimary};
  `}
`

const StyledFieldLabel = styled.span`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing.xxs};
  `}
`

const StyledInfoButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
`

const StyledActions = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: flex-end;
    gap: ${theme.spacing.xs};
  `}
`

const parseBound = (raw: string): number | undefined => {
  if (raw.trim() === '') return undefined

  const parsed = Number(raw)

  return Number.isFinite(parsed) ? parsed : undefined
}

const toFieldValue = (bound: number | undefined) =>
  bound === undefined ? '' : String(bound)

const renderFieldTooltip = (content: string) => (
  <Tooltip content={content}>
    <StyledInfoButton type="button" aria-label={content}>
      <Icon name="info" size={4} color="gray400" />
    </StyledInfoButton>
  </Tooltip>
)

const formatTriggerLabel = (
  label: string,
  { min, max }: FilterRangeValue,
  format: (value: number) => string
) => {
  if (min !== undefined && max !== undefined)
    return `${label}: ${format(min)}–${format(max)}`
  if (min !== undefined) return `${label}: ≥ ${format(min)}`
  if (max !== undefined) return `${label}: ≤ ${format(max)}`

  return label
}

// Numeric range filter for a filter row; both bounds optional, applied together
export const FilterRange = ({
  label,
  value,
  onChange,
  minFieldLabel,
  maxFieldLabel,
  minFieldTooltip,
  maxFieldTooltip,
  minPlaceholder,
  maxPlaceholder,
  invalidRangeText,
  formatValue = String,
  applyButtonText,
  clearButtonText,
  minAllowed = 0,
  maxAllowed,
  testId,
  placement = 'bottom-start',
}: FilterRangeProps) => {
  const { min: appliedMin, max: appliedMax } = value
  const [draft, setDraft] = useState<FilterRangeValue>(value)

  // an applied range can change outside the panel (URL state, reset on tab change)
  useEffect(
    () => setDraft({ min: appliedMin, max: appliedMax }),
    [appliedMin, appliedMax]
  )

  const isApplied = value.min !== undefined || value.max !== undefined
  const hasDraftBound = draft.min !== undefined || draft.max !== undefined
  const isDraftInverted =
    draft.min !== undefined && draft.max !== undefined && draft.min > draft.max

  // the browser only enforces min/max on stepper clicks, so typed-in bounds are checked here
  const isBoundOutOfRange = (bound: number | undefined) =>
    bound !== undefined &&
    (bound < minAllowed || (maxAllowed !== undefined && bound > maxAllowed))
  const isDraftOutOfRange =
    isBoundOutOfRange(draft.min) || isBoundOutOfRange(draft.max)

  return (
    <Popover>
      {({ open, close }) => (
        <div>
          <Float
            show={open}
            placement={placement}
            strategy="absolute"
            offset={8}
          >
            <StyledTrigger data-testid={testId}>
              <span>{formatTriggerLabel(label, value, formatValue)}</span>
              <Icon
                name={open ? 'chevron-up' : 'chevron-down'}
                size={3}
                color="black"
              />
            </StyledTrigger>
            <StyledPanel>
              <StyledFields>
                <StyledField>
                  <StyledFieldLabel>
                    {minFieldLabel}
                    {minFieldTooltip && renderFieldTooltip(minFieldTooltip)}
                  </StyledFieldLabel>
                  <TextInput
                    name="filter-range-min"
                    type="number"
                    placeholder={minPlaceholder}
                    min={minAllowed}
                    max={maxAllowed}
                    status={
                      isDraftInverted || isBoundOutOfRange(draft.min)
                        ? 'danger'
                        : undefined
                    }
                    value={toFieldValue(draft.min)}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        min: parseBound(event.target.value),
                      }))
                    }
                    data-testid={testId ? `${testId}-min` : 'filter-range-min'}
                  />
                </StyledField>
                <StyledField>
                  <StyledFieldLabel>
                    {maxFieldLabel}
                    {maxFieldTooltip && renderFieldTooltip(maxFieldTooltip)}
                  </StyledFieldLabel>
                  <TextInput
                    name="filter-range-max"
                    type="number"
                    placeholder={maxPlaceholder}
                    min={minAllowed}
                    max={maxAllowed}
                    status={
                      isDraftInverted || isBoundOutOfRange(draft.max)
                        ? 'danger'
                        : undefined
                    }
                    value={toFieldValue(draft.max)}
                    onChange={(event) =>
                      setDraft((previous) => ({
                        ...previous,
                        max: parseBound(event.target.value),
                      }))
                    }
                    data-testid={testId ? `${testId}-max` : 'filter-range-max'}
                  />
                </StyledField>
              </StyledFields>
              {invalidRangeText && (isDraftInverted || isDraftOutOfRange) && (
                <Feedback variant="critical">{invalidRangeText}</Feedback>
              )}
              <StyledActions>
                <Button
                  variant="tertiary"
                  disabled={!isApplied && !hasDraftBound}
                  onClick={() => {
                    setDraft({})
                    onChange({})
                    close()
                  }}
                  data-testid={
                    testId ? `${testId}-clear` : 'filter-range-clear'
                  }
                >
                  {clearButtonText}
                </Button>
                <Button
                  disabled={
                    !hasDraftBound || isDraftInverted || isDraftOutOfRange
                  }
                  onClick={() => {
                    onChange(draft)
                    close()
                  }}
                  data-testid={
                    testId ? `${testId}-apply` : 'filter-range-apply'
                  }
                >
                  {applyButtonText}
                </Button>
              </StyledActions>
            </StyledPanel>
          </Float>
        </div>
      )}
    </Popover>
  )
}
